import { IconOutlinedCalendar, IconClose } from "@/assets/icons";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { useScrollLockOnOpen } from "@/hooks/useScrollLockOnOpen";
import { DATE_FORMAT, YEAR_MONTH_STR_FORMAT, MONTH_FORMAT, MIN_YEAR_LIMIT, MAX_YEAR_LIMIT } from "@/constants/datetime";
import { background, black, blue, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { INVALID_DATE, INVALID_DATE_LIMIT_MAX, INVALID_DATE_LIMIT_MIN } from "@/constants/invalids";
import { Box, css, styled, FormHelperText } from "@mui/material";
import { DatePicker, DatePickerProps, DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers";
import { Dayjs, isDayjs } from "dayjs";
import { forwardRef, FC, useEffect, useMemo, useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useFormSchema } from "@/components/elements/Form/Form";

export type TInputDatePickerSize = "md" | "lg";

export type TInputDatePickerProps = {
  readonly?: boolean;
  previousValue?: Dayjs;
  nonControl?: boolean;
  previous?: boolean;
  placeholder?: string;
  error?: boolean;
  format?: string;
  enableCloseIcon?: boolean;
  onBlur?: (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, val?: Dayjs | null) => void;
  autoClamp?: boolean;
  label?: string;
  disableHelperText?: boolean;
} & Omit<DatePickerProps<Dayjs>, "size" | "readOnly">;

export const InputDatePicker = forwardRef<FC, TInputDatePickerProps>(
  (
    {
      readonly = false,
      previousValue,
      placeholder = "選択してください",
      nonControl = false,
      error,
      format = DATE_FORMAT,
      maxDate: maxDateProp,
      enableCloseIcon = true,
      onBlur,
      minDate: minDateProp,
      autoClamp = false,
      label,
      disableHelperText = false,
      ...rest
    },
    _ref,
  ) => {
    const { field, fieldState } = useFormField(nonControl);
    const { setError, clearErrors } = useFormContext() || {};
    const fieldsMeta = useFormSchema();

    const defaultMaxDate = useMemo(() => dayjs().year(MAX_YEAR_LIMIT), []);
    const defaultMinDate = useMemo(() => dayjs().year(MIN_YEAR_LIMIT).month(0).date(1), []);
    const maxDate = maxDateProp ?? defaultMaxDate;
    const minDate = minDateProp ?? defaultMinDate;

    const resolvedLabel = useMemo(() => {
      if (label) return label;
      if (field?.name && fieldsMeta[field.name]) {
        return fieldsMeta[field.name].label;
      }
      return "";
    }, [label, field?.name, fieldsMeta]);

    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isRangeError, setIsRangeError] = useState(false);
    const value = field?.value ?? rest.value;

    const resolvedMinDate = useMemo(() => {
      const absoluteMinDate = dayjs().year(MIN_YEAR_LIMIT).month(0).date(1);
      if (!minDate) return absoluteMinDate;
      return minDate.year() < MIN_YEAR_LIMIT ? absoluteMinDate : minDate;
    }, [minDate?.startOf("day").valueOf()]);

    const resolvedMaxDate = useMemo(() => {
      const absoluteMaxDate = dayjs().year(MAX_YEAR_LIMIT).month(11).date(31);
      if (!maxDate) return absoluteMaxDate;
      return maxDate.year() > MAX_YEAR_LIMIT ? absoluteMaxDate : maxDate;
    }, [maxDate?.startOf("day").valueOf()]);

    const clampDate = (date: Dayjs | null, force = false): Dayjs | null => {
      if (!autoClamp || !date || !date.isValid()) return date;
      if (!force) return date;
      if (resolvedMinDate && date.isBefore(resolvedMinDate)) {
        return resolvedMinDate;
      }
      if (resolvedMaxDate && date.isAfter(resolvedMaxDate)) {
        return resolvedMaxDate;
      }
      return date;
    };

    useEffect(() => {
      if (isDayjs(value)) {
        field?.onBlur();
      }
    }, [value]);

    // MUI's PickersPopper closes the calendar through a document-level (bubble-phase)
    // ClickAwayListener. Inside modals such as TalentInfoModal, ancestor onClick handlers
    // call e.stopPropagation() — which also stops the native event — so that listener
    // never fires and the calendar cannot be dismissed by clicking outside. Detect the
    // outside click ourselves in the CAPTURE phase: capture runs before any bubble-phase
    // stopPropagation anywhere in the document, so it is immune to that event blocking.
    // Selecting a date still closes via MUI's onClose (a direct React callback, not blocked).

    useEffect(() => {
      if (!open) return;
      const handleOutsidePointerDown = (e: PointerEvent) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        // Ignore clicks on the input area and inside the calendar popup itself.
        if (wrapperRef.current?.contains(target)) return;
        if (target.closest(".MuiPickersPopper-root")) return;
        setOpen(false);
      };
      document.addEventListener("pointerdown", handleOutsidePointerDown, true);
      return () => document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    }, [open]);

    useScrollLockOnOpen(open, wrapperRef);

    const clearValue = () => {
      field?.onChange(null);
      rest.onChange?.(null, {
        validationError: null,
      });
      field?.onBlur();
    };

    const handleChange = (value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => {
      const clampedValue = clampDate(value);
      field?.onChange(clampedValue);
      rest.onChange?.(clampedValue, context);
    };

    const isPrevious = useMemo(() => {
      if (fieldState?.isDirty === false) return true;
      const curValue = value;
      if (isDayjs(curValue) && isDayjs(previousValue)) {
        return curValue.format(DATE_FORMAT) == previousValue?.format(DATE_FORMAT);
      }
      return false;
    }, [fieldState?.isDirty, previousValue, field?.value, rest.value]);

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const clampedValue = clampDate(value, true);
      if (autoClamp && clampedValue && value && !clampedValue.isSame(value, "day")) {
        field?.onChange(clampedValue);
        rest.onChange?.(clampedValue, { validationError: null });
      }
      field?.onBlur();
      if (onBlur) {
        onBlur(e, clampedValue || value);
      }
    };

    const handleCalendarClose = () => {
      setOpen(false);
      field?.onBlur();
      onBlur?.();
    };

    const handleCalendarAccept = () => {
      field?.onBlur();
      onBlur?.();
    };

    const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (readonly || rest.disabled) return;
      // Guard on the current `open` state instead of a timer: React has not yet
      // flushed setOpen(false) from MUI's onClose within the same click, so when
      // a click is closing the calendar this handler still sees open === true and
      // bails out — preventing the immediate reopen race deterministically.
      if (open) return;
      if ((e.target as HTMLElement).tagName !== "INPUT") {
        setOpen(true);
      }
    };

    const handlePickerIconClick = () => {
      if (open) return;
      setOpen(true);
    };

    const handleClickClearIcon = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      clearValue();
    };

    useEffect(() => {
      const getErrorDetails = (): { errorText: string | null; rangeError: boolean } => {
        if (!value) return { errorText: null, rangeError: false };
        const d = dayjs(value);
        if (!d.isValid()) return { errorText: INVALID_DATE, rangeError: false };

        if (resolvedMinDate && d.isBefore(resolvedMinDate, "day")) {
          if (d.year() < MIN_YEAR_LIMIT) {
            return { errorText: INVALID_DATE_LIMIT_MIN, rangeError: false };
          }
          return { errorText: null, rangeError: true };
        }

        if (resolvedMaxDate && d.isAfter(resolvedMaxDate, "day")) {
          if (d.year() > MAX_YEAR_LIMIT) {
            return { errorText: INVALID_DATE_LIMIT_MAX, rangeError: false };
          }
          return { errorText: null, rangeError: true };
        }

        return { errorText: null, rangeError: false };
      };

      const { errorText, rangeError } = getErrorDetails();
      setIsRangeError(rangeError);

      let errorMsg: string | null = null;
      if (errorText) {
        const pathName = resolvedLabel ? String(resolvedLabel) : "";
        errorMsg = pathName
          ? errorText.replace("{{path}}", pathName)
          : errorText.replace("「{{path}}」の", "").replace("「{{path}}」は", "").replace("「{{path}}」", "").trim();
      }

      setValidationError(errorMsg);

      if (!nonControl && field?.name) {
        if ((errorMsg || rangeError) && setError) {
          setError(field.name, { type: "custom", message: errorMsg || "" });
        } else if (!errorMsg && !rangeError && clearErrors) {
          clearErrors(field.name);
        }
      }
    }, [value, resolvedMinDate, resolvedMaxDate, resolvedLabel, nonControl, field?.name, setError, clearErrors]);

    const handleDatePickerError = (reason: DateValidationError, val: Dayjs | null) => {
      rest.onError?.(reason, val);
    };

    return (
      <InputDatePickerWrap previous={isPrevious}>
        <DatePickerInputArea ref={wrapperRef} onClick={handleWrapperClick}>
          <PickerIcon onClick={handlePickerIconClick} sx={{ pointerEvents: readonly || rest.disabled ? "none" : "" }}>
            <IconOutlinedCalendar color={black[80]} />
          </PickerIcon>
          {isDayjs(value) && !rest.disabled && !readonly && enableCloseIcon && (
            <ClearIcon onClick={handleClickClearIcon}>
              <IconClose fontSize="20px" color={text.primary} />
            </ClearIcon>
          )}
          <DatePicker
            slotProps={{
              field: { clearable: false },
              textField: {
                placeholder: placeholder,
                error: Boolean(fieldState?.error) || Boolean(validationError) || isRangeError || error,
                onBlur: handleInputBlur,
              },
              calendarHeader: {
                format: YEAR_MONTH_STR_FORMAT,
                sx: {
                  display: format === MONTH_FORMAT ? "none" : "flex",
                },
              },
              popper: {
                placement: "bottom-start",
              },
            }}
            format={format}
            disableOpenPicker
            closeOnSelect
            readOnly={readonly}
            maxDate={resolvedMaxDate}
            minDate={resolvedMinDate}
            {...rest}
            {...field}
            open={open}
            onClose={handleCalendarClose}
            onAccept={handleCalendarAccept}
            onError={handleDatePickerError}
            value={value ?? null}
            onChange={handleChange}
          />
        </DatePickerInputArea>
        {nonControl && Boolean(validationError) && !disableHelperText && (
          <FormHelperText error sx={{ marginTop: "4px", marginLeft: "4px", lineHeight: 1.2, whiteSpace: "pre-line" }}>
            {validationError}
          </FormHelperText>
        )}
      </InputDatePickerWrap>
    );
  },
);

const options = { shouldForwardProp: (propName: string) => !["previous"].includes(propName) };

const DatePickerInputArea = styled(Box)`
  position: relative;
`;

const InputDatePickerWrap = styled(Box, options)<{ previous: boolean }>`
  position: relative;
  .MuiTextField-root {
    width: 100%;
  }
  .MuiInputBase-root {
    background: ${background.white};
    cursor: pointer;
    padding: 6px 0px;
    padding-left: 28px;
    width: 100%;
    border-radius: ${rounded.xs};
    input {
      padding: 0;
      cursor: text;
      color: ${text.primary};
      caret-color: ${blue[70]};
      ${css(typography.body14)}
      &::placeholder {
        opacity: 1;
        color: ${text.tertiary}!important;
      }
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }

    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      cursor: default;
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    &.Mui-readOnly {
      background: ${background.disable};
      pointer-events: none;
      cursor: default;
      input {
        color: ${text.primary}!important;
        &::placeholder {
          opacity: 0;
        }
      }
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    ${({ previous }) => previous && PreviousInputDatePicker};
  }
`;

const PreviousInputDatePicker = css`
  input {
    color: ${lightBlue[60]};
  }
`;

const PickerIcon = styled(IconBox)`
  position: absolute;
  top: 50%;
  left: 3px;
  transform: translateY(-50%);
  z-index: 1;
`;

const ClearIcon = styled(IconBox)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 2;
`;
